import React, {useEffect} from 'react';
import {Grid} from '@mui/material';
import {
  BuilderColumnWrapper,
  BuilderSourceColumnHeader,
  BuilderDestinationColumnHeader,
} from '../CreateBuilder/BuilderSourceDestination/BuilderSourceDestination';
import {BuilderPlanFormsStyled} from '../CreateBuilder/BuilderSourceDestination/BuilderPlanForm';
import {BuilderProps} from '../CreateBuilder/BuilderSourceDestination/builderValidators';
import {BuilderParserStep} from '../BuilderParserStep/BuilderParserStep';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {modifyFieldsType} from './builderModifyFieldTypes';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {useResetBuilder} from '../CreateBuilder/BuilderSourceDestination/useResetBuilder';
import {useBuilderContext} from '../CreateBuilder/useBuilderContext';
import {BuilderModifyFields} from './BuilderModifyFields';
import {useAutoForm, useAutoField} from '../../../components/AutoForm/useAutoForm';
import {UPDATES} from '../../../shared/UPDATES';
import {
  builderNoteToFields,
  builderModifyChanges,
  changeModifySourceFactory,
} from '../hooks/builderModifyHooks';
import {useModifyPotsQuery} from './useModifyPotsQuery';
import {ModifySourceParent} from './ModifySourceParent';
import {PotDetails} from '../../../schema-types/PotDetails';

export {builderNoteToFields, builderModifyChanges};

export const BuilderModify: React.FC<BuilderProps> = ({sourceDestinationLabels}) => {
  const {values, setFieldValue} = useAutoForm<builderFieldsType>();
  useResetBuilder(OVRecommendActionEnum.MODIFY);
  const {builderReviewId} = useBuilderContext();
  const [modifyFieldObj, modifyMeta] =
    useAutoField<builderFieldsType['modify']>('modify');
  const [, modifySourceMeta, modifySourceHelpers] = useAutoField<
    modifyFieldsType['source'] | undefined
  >('modify.source');

  const {sourcePots} = useModifyPotsQuery();

  const changeModifySource = changeModifySourceFactory({sourcePots, modifySourceHelpers});

  useEffect(() => {
    const id = values.modify?.source?.id;
    const source = sourcePots.find((x) => x.id === id);
    if (source) {
      setFieldValue(UPDATES._650, `modify.planProvider`, source?.productProvider?.id);
      setFieldValue(UPDATES._651, `modify.planType`, source?.productName?.id);
      setFieldValue(
        UPDATES._652,
        `modify.existingModel`,
        source?.modelPortfolio || source.mandate
      );
    } else {
      setFieldValue(UPDATES._653, `modify.planProvider`, undefined);
      setFieldValue(UPDATES._654, `modify.planType`, undefined);
      setFieldValue(UPDATES._655, `modify.existingModel`, undefined);
    }
  }, [values.modify?.source]);

  const sourcePot: PotDetails | undefined = sourcePots.reduce<PotDetails | undefined>(
    (acc, x) => {
      if (x.id === modifyFieldObj.value?.source.id) {
        return x;
      }
      const childPot = x.dentistChildren?.find(
        (y) => y.id === modifyFieldObj.value?.source.id
      );
      if (childPot) {
        return childPot;
      }
      return acc;
    },
    undefined
  );

  return sourcePots?.length === 0 ? (
    <BuilderParserStep errorText="No plans to modify" />
  ) : (
    <BuilderColumnWrapper>
      <Grid item xs={6} xl={5.8} data-test="Source Choices">
        <BuilderSourceColumnHeader
          fromError={modifySourceMeta.error}
          sourceLabel={sourceDestinationLabels?.first}
        />
        <BuilderPlanFormsStyled>
          {sourcePots.map((pot) => (
            <ModifySourceParent
              key={pot.id}
              pot={pot}
              selectedId={modifyFieldObj.value?.source.id}
              sameReview={pot.review?.id === builderReviewId}
              handleChange={changeModifySource}
            />
          ))}
        </BuilderPlanFormsStyled>
      </Grid>
      <Grid item xs={6} xl={5.8} data-test="Destination Choices">
        <BuilderDestinationColumnHeader
          toError={modifyMeta.error}
          destinationLabel="Changes to make"
        />
        <BuilderModifyFields sourcePot={sourcePot} />
      </Grid>
    </BuilderColumnWrapper>
  );
};
